import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FullPlanOverlay from './FullPlanOverlay';
import MeetingPrepIntro from './MeetingPrepIntro';
import { downloadPackHtml } from './packExport';
import './MeetingPrepPack.css';

function Points({ values }) {
  return <ul>{values.map((value, index) => <li key={index}>{value}</li>)}</ul>;
}
function Guide({ slide }) {
  if (!slide) return <p>Choose a saved slide to see its talking points.</p>;
  const guide = slide.guide;
  return <section className="mpp-guide">
    {slide.stale && <p className="mpp-notice">This slide has changed since these notes were written.</p>}
    {guide ? <>
      <h3>Talking points</h3><Points values={guide.talking_points} />
      {!!guide.if_pushed.length && <><h3>If they push back</h3>{guide.if_pushed.map((item, index) =>
        <div className="mpp-response" key={index}><strong>{item.objection}</strong><p>{item.response}</p></div>)}</>}
      <h3>Transition</h3><p>{guide.transition}</p>
    </> : <p>Update the pack to prepare notes for this slide.</p>}
  </section>;
}
function Brief({ brief }) {
  return <section className="mpp-brief"><h2>Meeting brief</h2>
    <h3>Objective</h3><p>{brief.objective}</p>
    <h3>Main argument</h3><p>{brief.main_argument}</p>
    <h3>Strongest evidence</h3><Points values={brief.strongest_evidence} />
    <h3>Key asks</h3><Points values={brief.key_asks} />
  </section>;
}

// The sourcing negotiation brief (Lane 1354 S4): server-written OK-to-say lines, then the
// private coaching. Every string arrives rendered by the server; nothing is computed here.
function Mark({ ok }) {
  return <span className={`mpp-mark ${ok ? 'mpp-mark--ok' : 'mpp-mark--private'}`}>{ok ? 'OK to say' : 'Private: do not say'}</span>;
}
function Written({ item }) {
  return <p>{item.text}{item.fallback && <em className="mpp-rules"> (Written by Wingman's rules)</em>}</p>;
}
function day(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}
function NegotiationBrief({ brief }) {
  return <section className="mpp-nb" data-testid="negotiation-brief"><h2>Negotiation brief</h2>
    <p className="mpp-asof">Offers and targets as of {day(brief.commercial_as_of)} (the deck's). Supplier intelligence as of {day(brief.intel_as_of)}. A rewrite keeps the deck's offers and targets.</p>
    {!!brief.ok_to_say.length && <><h3><Mark ok /></h3><Points values={brief.ok_to_say} /></>}
    <h3><Mark /></h3>
    <h4>Approach</h4><Written item={brief.approach} />
    {!!brief.points.length && <><h4>Strategy</h4><Points values={brief.points} /></>}
    {!!brief.gap_drivers.length && <><h4>Where the gap is</h4><Points values={brief.gap_drivers} /></>}
    {!!brief.pushbacks.length && <><h4>If they push back</h4>{brief.pushbacks.map((item, index) =>
      <div className="mpp-response" key={index}><strong>{item.objection}</strong><p>{item.answer}</p></div>)}</>}
    {!!brief.concessions.length && <><h4>What we can give</h4><Points values={brief.concessions} /></>}
    <h4>Walk-away</h4><Written item={brief.walk_away} />
    {!!brief.left_out && <p className="mpp-notice">{brief.left_out} {brief.left_out === 1 ? 'point was' : 'points were'} left out because {brief.left_out === 1 ? 'it' : 'they'} could not be checked.</p>}
  </section>;
}
function ApproachLine({ brief }) {
  return <p className="mpp-approach" data-testid="negotiation-approach"><Mark /> <strong>Approach:</strong> {brief.approach.text}</p>;
}
const BRIEF_PENDING = ['queued', 'running'];

export default function MeetingPrepPack({ compositionId, apiBase, authHeaders, onlySlideIndex,
  savedSignature, dirty, onSave, onStatus, introKey, workspaceReady, onOpenNotes, notesToggleRef, headerTarget,
  readOnly = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [illustrated, setIllustrated] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewRetry, setPreviewRetry] = useState(0);
  const opener = useRef(null);
  const alive = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const props = useRef({ authHeaders, onSave, onStatus, compositionId });
  props.current = { authHeaders, onSave, onStatus, compositionId };
  const base = `${apiBase}/api/decks/compositions/${compositionId}/prep-pack`;
  const refresh = useCallback(async (epoch) => {
    const response = await fetch(base, { headers: props.current.authHeaders() });
    if (!response.ok) throw new Error('The meeting prep pack could not be loaded. Try again.');
    const next = await response.json();
    if (mounted.current && alive.current === epoch) { setData(next); setError(''); }
    return next;
  }, [base]);
  useEffect(() => {
    const epoch = ++alive.current;
    setData(null); setError(''); setExpanded(false);
    const poll = async () => {
      try {
        await refresh(epoch);
      } catch (e) { if (alive.current === epoch) setError(e.message); }
    };
    if (compositionId) poll();
    return () => { ++alive.current; };
  }, [compositionId, refresh, savedSignature]);
  const pending = working || ['queued', 'running'].includes(data?.status);
  const nb = data?.negotiation_brief;
  const briefPending = BRIEF_PENDING.includes(nb?.status);
  const brief = nb?.brief || null;
  useEffect(() => {
    props.current.onStatus?.({ loading: !data && !error, enriching: pending || briefPending,
      ready: !!data?.pack || !!brief });
  }, [data, error, pending, briefPending, brief]);
  const generate = async () => {
    const isCurrent = () => mounted.current && props.current.compositionId === compositionId;
    setWorking(true); setError('');
    try {
      // Save through the workspace's existing serialized writer and fit gate.
      const id = await props.current.onSave();
      if (!id || !isCurrent()) return;
      const current = await fetch(base, { headers: props.current.authHeaders() });
      if (!current.ok) throw new Error('Could not check the saved deck. Try again.');
      const state = await current.json();
      if (!isCurrent()) return;
      const response = await fetch(`${base}/generate`, { method: 'POST', headers: props.current.authHeaders(),
        body: JSON.stringify({ base_revision: state.edit_revision }) });
      if (!response.ok) {
        const problem = await response.json();
        throw new Error(typeof problem.detail === 'string' ? problem.detail : 'The prep pack could not be started.');
      }
      // Save can reset the polling epoch; only update this still-mounted component.
      if (isCurrent()) await refresh(alive.current);
    } catch (e) { if (isCurrent()) setError(e.message); }
    finally { if (isCurrent()) setWorking(false); }
  };
  // A POST can start after the read effect settled. Poll that newly queued job too.
  useEffect(() => {
    if (!['queued', 'running'].includes(data?.status) && !BRIEF_PENDING.includes(data?.negotiation_brief?.status)) return undefined;
    const epoch = alive.current;
    const timer = setTimeout(() => refresh(epoch).catch(e => {
      if (alive.current === epoch) setError(e.message);
    }), 3000);
    return () => clearTimeout(timer);
  }, [data, refresh]);
  const download = async () => {
    const isCurrent = () => mounted.current && props.current.compositionId === compositionId;
    setError('');
    try {
      const id = await props.current.onSave();
      if (id && isCurrent()) await downloadPackHtml({ compositionId: id, apiBase, authHeaders: props.current.authHeaders, native: true, isCurrent });
    } catch (e) { if (isCurrent()) setError(e.message); }
  };
  const close = useCallback(() => setExpanded(false), []);
  useEffect(() => {
    setIllustrated(null); setPreviewError('');
    if (!expanded || dirty) return undefined;
    const controller = new AbortController();
    fetch(`${base}/preview`, { headers: props.current.authHeaders(), signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('The illustrated pack could not be loaded.');
        const next = await response.json();
        if (!controller.signal.aborted) setIllustrated(next);
      }).catch(failure => { if (!controller.signal.aborted) setPreviewError(failure.message); });
    return () => controller.abort();
  }, [base, expanded, dirty, savedSignature, previewRetry, data?.pack?.created_at, nb?.created_at]);
  const pack = data?.pack;
  // One key per rewrite click, kept until the server answered, so a retry after a lost
  // answer can never start a second paid attempt.
  const rewriteKey = useRef(null);
  const [rewriting, setRewriting] = useState(false);
  const rewrite = async () => {
    if (rewriting) return;
    setError(''); setRewriting(true);
    if (!rewriteKey.current) {
      rewriteKey.current = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    }
    try {
      const response = await fetch(`${apiBase}/api/decks/compositions/${compositionId}/negotiation-brief/generate`,
        { method: 'POST', headers: { ...props.current.authHeaders(), 'Idempotency-Key': rewriteKey.current } });
      rewriteKey.current = null;          // answered: the next click is a new rewrite
      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new Error(typeof problem.detail === 'string' ? problem.detail : 'The brief could not be started.');
      }
      if (mounted.current) await refresh(alive.current);
    } catch (e) { if (mounted.current) setError(e.message); }
    finally { if (mounted.current) setRewriting(false); }
  };
  const actions = <div className="mpp-actions">
    {!headerTarget && !expanded && (pack || brief) && <button type="button" ref={opener} onClick={() => setExpanded(true)}>Open full prep pack</button>}
    {expanded && (pack || brief) && <button type="button" onClick={download} disabled={working}>Download internal pack</button>}
    {/* Lane 1412: a colleague reads the builder's pack; only the builder writes it. */}
    {!readOnly && nb && !briefPending && (expanded || (!brief && nb.status === 'failed')) &&
      <button type="button" onClick={rewrite} disabled={rewriting}>{brief ? 'Rewrite negotiation brief' : 'Try the negotiation brief again'}</button>}
    {!readOnly && (!pack || pack.stale || data?.status === 'failed') && <button type="button" onClick={generate} disabled={pending}>
      {pending ? 'Writing prep pack…' : pack ? 'Update prep pack' : data?.status === 'failed' ? 'Try again' : 'Generate prep pack'}
    </button>}
  </div>;
  const notice = <>
    {error && <p className="mpp-notice" role="alert">{error} <button type="button" onClick={() => refresh(alive.current).catch(e => setError(e.message))}>Reload</button></p>}
    {pending && <p className="mpp-progress" role="status"><span />{data?.status === 'queued' ? 'Your internal prep pack is queued.' : 'Writing talking points and responses for this deck…'} The deck is ready to use.</p>}
    {data?.status === 'failed' && <p className="mpp-notice">The latest prep attempt could not finish. {pack ? 'Your previous notes are still available.' : 'The deck is unaffected.'} No new AI call will start until you try again.</p>}
    {dirty && <p className="mpp-notice">Save your deck changes to see the notes for the current slides.</p>}
    {(expanded ? illustrated?.pack?.stale : pack?.stale) && <p className="mpp-notice">The deck has changed. Review the marked notes, or update the prep pack when ready.</p>}
    {briefPending && <p className="mpp-progress" role="status"><span />Writing the negotiation brief…</p>}
    {nb?.status === 'failed' && <p className="mpp-notice">The latest negotiation brief attempt could not finish. {brief ? 'The previous brief is still shown.' : 'The deck is unaffected.'}</p>}
  </>;
  const content = <div className={`mpp ${expanded ? 'mpp-full' : ''}`} data-testid="meeting-prep-pack">
    {expanded && <header><div><span className="mpp-eyebrow">Internal only</span><h2>Meeting prep pack</h2></div>
      <button type="button" onClick={close} aria-label="Close prep pack">Close</button>
    </header>}
    {actions}{notice}
    {!data && !error && <p role="status">Loading saved prep…</p>}
    {data && !pack && !pending && data.status !== 'failed' && <p>Talking points, useful responses to pushback, and transitions, based on your saved deck.</p>}
    {(pack || brief) && !dirty && (expanded ? <>
      {!illustrated && !previewError && <p role="status">Loading your slides and saved talking points…</p>}
      {previewError && <p role="alert">{previewError} <button type="button" onClick={() => setPreviewRetry(value => value + 1)}>Try loading again</button></p>}
      {illustrated?.negotiation_brief?.brief && <NegotiationBrief brief={illustrated.negotiation_brief.brief} />}
      {illustrated?.pack && <><Brief brief={illustrated.pack.meeting_brief} />{illustrated.pack.slides.map((slide, index) =>
        <article className="mpp-slide" key={`${slide.page_id}-${index}`}><h2>Slide {slide.number} · {slide.title}</h2>
          <div className="mpp-slide-layout"><figure className="mpp-slide-visual">
            {slide.image ? <img src={slide.image} alt={`Slide ${slide.number}: ${slide.title}`} loading="lazy" decoding="async" width="1920" height="1080" />
              : <div className="mpp-image-placeholder">Slide image unavailable. Close and reopen the pack to try again.</div>}
            <figcaption>Slide {slide.number}</figcaption></figure><Guide slide={slide} /></div>
        </article>)}</>}
      </> : <>{brief && <ApproachLine brief={brief} />}{pack && <Guide slide={pack.slides[onlySlideIndex]} />}</>)}
  </div>;
  return <>
    {headerTarget && (pack || brief) && createPortal(<button ref={opener} type="button" className="dk-btn sm"
      onClick={() => setExpanded(true)}>Open full prep pack</button>, headerTarget)}
    {introKey && <MeetingPrepIntro key={introKey} storageKey={introKey} data={data}
      workspaceReady={workspaceReady && !expanded} onOpenNotes={onOpenNotes} returnFocusRef={notesToggleRef} />}
    {expanded ? <FullPlanOverlay onClose={close} returnFocusRef={opener} label="Meeting prep pack">{content}</FullPlanOverlay> : content}
  </>;
}
